type Suggestion = {
  title: string;
  label: string;
  prompt: string;
};

// ---------------------------------------------------------------------------
// Reusable suggestions (composed into page-specific sets)
// ---------------------------------------------------------------------------

const optimizeGuidances: Suggestion = {
  title: 'Optimize my guidances',
  label: 'using ticket data and templates',
  prompt:
    'Review my existing AI Agent guidances and draft improved versions that reduce handovers. Use these as source material: (1) my current guidances, (2) my published flows, (3) human-handled tickets from the last 90 days. Follow the structure defined by guidance templates.',
};

const createGuidances: Suggestion = {
  title: 'Create my first set of guidances',
  label: 'using best-practice templates',
  prompt:
    'Please create the starter set of draft guidances for my account using the recommended best-practice templates. Keep closely to the template set and do not introduce extra guidances.',
};

const analyzePerformance: Suggestion = {
  title: 'Analyze AI Agent performance',
  label: 'and find improvement areas',
  prompt:
    'Analyze my AI Agent performance metrics — automation rate, coverage, handovers — broken down by channel and intent. Identify where it performs well and where there is room for improvement.',
};

const ticketVolume: Suggestion = {
  title: 'Show ticket volume trends',
  label: 'with a visual report',
  prompt:
    'Create an HTML report showing my ticket volume trends over the last 90 days, broken down by channel and intent category. Include key metrics and insights.',
};

const convertFlows: Suggestion = {
  title: 'Convert my flows to guidances',
  label: 'migrate published flows',
  prompt: 'Convert my published flows to guidances.',
};

const latestFeatures: Suggestion = {
  title: 'What\'s new in AI Agent?',
  label: 'latest features and how to use them',
  prompt:
    'What are the newest AI Agent features available and how can I leverage them to improve my customer support?',
};

// ---------------------------------------------------------------------------
// Page-specific suggestions (matched by URL patterns)
// ---------------------------------------------------------------------------

const pageSuggestions: { pattern: string; suggestions: Suggestion[] }[] = [
  // Overview
  {
    pattern: '/overview',
    suggestions: [
      analyzePerformance,
      ticketVolume,
      optimizeGuidances,
      latestFeatures,
    ],
  },
  // Intents
  {
    pattern: '/intents',
    suggestions: [
      { title: 'Analyze my top intents', label: 'volume, automation, and trends', prompt: 'Analyze my top customer intents by volume and automation rate. Which intents have the most room for improvement?' },
      { title: 'What are customers asking about?', label: 'common themes and patterns', prompt: 'What are the most common things customers are asking about? Pull real ticket subjects to show the themes.' },
      optimizeGuidances,
      analyzePerformance,
    ],
  },
  // Opportunities
  {
    pattern: '/opportunities',
    suggestions: [
      { title: 'Review my opportunities', label: 'and help me prioritize', prompt: 'List my current AI Agent opportunities and help me prioritize which ones to address first based on impact.' },
      { title: 'Process top opportunities', label: 'approve or dismiss with reasoning', prompt: 'Review my top pending opportunities and help me decide which to approve and which to dismiss, with data-backed reasoning.' },
      optimizeGuidances,
      analyzePerformance,
    ],
  },
  // Tone of voice
  {
    pattern: '/tone-of-voice',
    suggestions: [
      { title: 'Help me refine my tone', label: 'based on brand and audience', prompt: 'Help me refine my AI Agent tone of voice. Review the current settings and suggest improvements based on my brand and customer base.' },
      { title: 'Preview tone changes', label: 'test before applying', prompt: 'Show me a preview of how AI Agent would respond with different tone settings so I can compare.' },
      analyzePerformance,
      latestFeatures,
    ],
  },
  // Knowledge / Guidance
  {
    pattern: '/knowledge',
    suggestions: [
      optimizeGuidances,
      createGuidances,
      convertFlows,
      { title: 'Audit my knowledge base', label: 'find gaps and overlaps', prompt: 'Review my current knowledge sources (guidances, articles, URLs) and identify gaps where customers are asking questions we don\'t cover, and overlaps where multiple sources conflict.' },
    ],
  },
  // Actions
  {
    pattern: '/actions',
    suggestions: [
      { title: 'What actions can I add?', label: 'browse available templates', prompt: 'What action templates are available that I haven\'t deployed yet? Help me understand which ones would be most valuable for my store.' },
      { title: 'Review my active actions', label: 'check usage and errors', prompt: 'Review my currently enabled AI Agent actions. Are there any that are failing or underperforming?' },
      analyzePerformance,
      latestFeatures,
    ],
  },
  // Sales / Shopping Assistant
  {
    pattern: '/sales',
    suggestions: [
      { title: 'How is Shopping Assistant performing?', label: 'conversion and engagement metrics', prompt: 'Analyze my Shopping Assistant performance — how is it impacting conversion rates and customer engagement?' },
      { title: 'Improve product recommendations', label: 'based on customer behavior', prompt: 'Help me improve my Shopping Assistant product recommendations based on customer browsing and purchase patterns.' },
      analyzePerformance,
      latestFeatures,
    ],
  },
  // Deploy / Channels
  {
    pattern: '/deploy',
    suggestions: [
      { title: 'Compare channel performance', label: 'chat vs email vs SMS', prompt: 'Compare my AI Agent performance across channels — chat, email, and SMS. Where is automation strongest and where does it need work?' },
      analyzePerformance,
      optimizeGuidances,
      latestFeatures,
    ],
  },
  // Test / Playground
  {
    pattern: '/test',
    suggestions: [
      { title: 'Help me test a scenario', label: 'simulate a customer conversation', prompt: 'Help me test my AI Agent by simulating a common customer scenario. What are the most frequent questions I should test?' },
      optimizeGuidances,
      analyzePerformance,
      latestFeatures,
    ],
  },
  // Settings
  {
    pattern: '/settings',
    suggestions: [
      { title: 'Review my AI Agent setup', label: 'check configuration', prompt: 'Review my AI Agent configuration and let me know if there are any settings I should adjust for better performance.' },
      analyzePerformance,
      optimizeGuidances,
      latestFeatures,
    ],
  },
];

// Default for AI Agent pages not matching a specific pattern
const defaultAiAgentSuggestions: Suggestion[] = [
  analyzePerformance,
  optimizeGuidances,
  ticketVolume,
  latestFeatures,
];

// Default for non-AI-Agent pages (tickets, macros, etc.)
const defaultSuggestions: Suggestion[] = [
  ticketVolume,
  analyzePerformance,
  optimizeGuidances,
  createGuidances,
];

/**
 * Returns up to 4 suggestions based on the current page URL.
 * Matches against AI Agent sub-pages first, falls back to defaults.
 */
export function getSuggestionsForPage(pageUrl: string): Suggestion[] {
  if (!pageUrl.includes('app/ai-agent/')) {
    return defaultSuggestions;
  }

  // Match the most specific page pattern
  for (const { pattern, suggestions } of pageSuggestions) {
    if (pageUrl.includes(pattern)) {
      return suggestions;
    }
  }

  return defaultAiAgentSuggestions;
}
